import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { useMutation } from '@apollo/client';
import { Button, Stack, Typography } from '@mui/material';
import { IconChevron } from '../../../../../../assets/icons/icon-chevron';
import { DialogSelect } from '../../../../../../components/dialog/select';
import { InputMui } from '../../../../../../components/input-mui/input-mui';
import { useInstallationContext } from '../../../../../../context/installation';
import {
  ChangePreviewTariffMutation,
  ChangePreviewTariffMutationVariables,
  InstallationAttachmentError,
  InstallationError,
  TariffKindInternal,
  UserPermissionType,
} from '../../../../../../data-access/gql-types/graphql';
import { CHANGE_PREVIEW_TARIFF } from '../../../../../../data-access/mutations/installations';
import { useInstallationAttachementsErrors } from '../../../../../../hooks/errors/use-installation-attachement-errors';
import { useMutationErrors } from '../../../../../../hooks/errors/use-mutation-errors';
import { useUserPermissions } from '../../../../../../hooks/user/use-user-permissions';
import { ROUTES } from '../../../../../../routes/routes';
import { MutationErrorType } from '../../../../../../types/common';
import { TariffName, TariffOperator } from '../../../channel-details/optimizer/configuration/types/tariff-kind';
import {
  operatorLogos,
  tariffKind,
  tariffsForRequest,
} from '../../../channel-details/optimizer/configuration/utils/tariff-kind';
import { AnimatedBox } from '../../components/animated-box/animated-box';
import { LogosDialog } from '../../components/logos-dialog/logos-dialog';
import { DashboardHoursByTariff } from '../../components/tariff-hours/hours-by-tariff';
import { useDashboardContext } from '../../context/dashboard-context';
import { useTariffs } from '../../hooks/use-tariffs';

export const DashboardTariff: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { tariff, setTariff, tariffOperator, setTariffOperator } = useDashboardContext();
  const { tariffs, operator, setOperator } = useTariffs();
  const [showLogosDialog, setShowLogosDialog] = useState<boolean>(false);
  const [showTariffsDialog, setShowTariffsDialog] = useState<boolean>(false);
  const { permissions } = useUserPermissions();
  const { selectedInstallationId, selectedInstallation, installationList, updateInstallationList } =
    useInstallationContext();
  const installationErrors = useMutationErrors(MutationErrorType.Installation);
  const installationAttachementsErrors = useInstallationAttachementsErrors();
  const [changePreviewTariff, { loading }] = useMutation<
    ChangePreviewTariffMutation,
    ChangePreviewTariffMutationVariables
  >(CHANGE_PREVIEW_TARIFF);

  const canConfigureDashboardPreviewTariff = permissions[UserPermissionType.ConfigureDashboardPreviewTariff];
  const tariffName = tariffs.find((x) => x.value === tariff)?.label;
  const operatorData = operatorLogos.find((x) => x.operator === tariffOperator);

  useEffect(() => {
    if (selectedInstallation?.previewTariff) {
      const tariff = tariffKind[selectedInstallation.previewTariff];
      setTariff(tariff.tariff as TariffName);
      setTariffOperator(tariff.operator as TariffOperator);
    }
  }, [selectedInstallation?.previewTariff]);

  const handleChangePreviewTariff = (tariffName: TariffName) => {
    setTariff(tariffName);
    setTariffOperator(operator);
    setShowTariffsDialog(false);

    const previewTariff = tariffsForRequest.find((x) => x.operator === operator && x.tariff === tariffName)
      ?.enum as TariffKindInternal;
    if (!previewTariff) return;

    changePreviewTariff({
      variables: {
        input: {
          installationId: selectedInstallationId,
          previewTariff,
        },
      },
      onCompleted: (data) => {
        if (!data || !data.changePreviewTariff) return;

        if (data.changePreviewTariff.result?.ok) {
          const tempList = cloneDeep(installationList);
          const foundInstallationIndex = tempList.findIndex(
            (installation) => installation.installationId === selectedInstallationId,
          );

          if (foundInstallationIndex !== -1) {
            tempList[foundInstallationIndex].previewTariff = previewTariff;
            updateInstallationList(tempList);
          }
        } else {
          installationErrors.handleErrors((data.changePreviewTariff.errors || []) as InstallationError[]);
          installationAttachementsErrors.handleErrors(
            (data.changePreviewTariff.errors || []) as InstallationAttachmentError[],
          );
        }
      },
    });
  };

  const handleOperatorSelect = (operator: TariffOperator) => {
    setOperator(operator);
    setShowLogosDialog(false);
    setShowTariffsDialog(true);
  };

  const handleInputClick = () => {
    if (canConfigureDashboardPreviewTariff && !loading) {
      setShowLogosDialog(true);
    }
  };

  return (
    <AnimatedBox marginBottom={6} backgroundColor="var(--color-element)">
      {tariff && (
        <Button
          sx={{ width: '100%', padding: '16px 24px', marginBottom: 1 }}
          onClick={() => navigate(ROUTES.TariffOverview())}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" maxWidth={600}>
            <Stack direction="row" alignItems="center">
              <Typography sx={{ fontSize: 16, marginRight: 1 }}>{`${t('tariff.tariffOverview')} `}</Typography>
              {operatorData?.imagePath ? (
                <img src={operatorData.imagePath} alt={operatorData.name} height="45px" width="auto" />
              ) : null}
            </Stack>
            <IconChevron direction="right" noSpace />
          </Stack>
        </Button>
      )}

      <Stack paddingLeft={3} paddingRight={3} paddingTop={tariff ? 0 : 3} alignItems="center">
        <Stack alignItems="center" maxWidth="600px" width="100%">
          <InputMui
            value={tariffName || ''}
            readOnly
            onClickInput={handleInputClick}
            endIcon={<IconChevron direction="right" />}
            reverse
            label={t('tariff.tariff')}
            disabled={!canConfigureDashboardPreviewTariff || loading}
          />
          <LogosDialog
            show={showLogosDialog}
            setShow={setShowLogosDialog}
            onOperatorSelect={handleOperatorSelect}
            currentOperator={operator}
          />
          <DialogSelect
            show={showTariffsDialog}
            setShow={setShowTariffsDialog}
            title={t('tariff.tariff')}
            options={tariffs}
            value={tariff || undefined}
            onChange={handleChangePreviewTariff}
          />
        </Stack>
      </Stack>

      <DashboardHoursByTariff />
    </AnimatedBox>
  );
};
